import React from "react";
import { Helmet } from "react-helmet-async";
import { MdPlaylistAdd } from "react-icons/md";

import styles from "./ComplexOrderDetails.module.scss";
import { useComplexOrderDetails } from "./useComplexOrderDetails";
import { PageHeader } from "../../../../common/components/pageHeader/PageHeader";
import { EmptyState } from "../../../../common/components/emptyState/EmptyState";
import {
  getComplexOrderStatusText,
  getComplexOrderStatusTextColor,
} from "../../../../common/utils/functions/enumToText";
import { ComplexOrderStatus } from "../../../../common/types/enums";

const formatDate = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

export const ComplexOrderDetails: React.FC = () => {
  const { order, ready } = useComplexOrderDetails();

  if (!ready) return null;

  if (!order) {
    return (
      <div className={styles.Page}>
        <EmptyState
          icon={MdPlaylistAdd}
          title="Проект не найден"
          description="Возможно, он был удалён."
        />
      </div>
    );
  }

  const status = order.status as ComplexOrderStatus;

  const items = order.items || [];
  const parents = items.filter((i: any) => !i.parentId);
  const childrenMap: Record<string, any[]> = {};
  items.forEach((i: any) => {
    if (i.parentId) {
      if (!childrenMap[i.parentId]) childrenMap[i.parentId] = [];
      childrenMap[i.parentId].push(i);
    }
  });
  const hasTree = parents.some((p: any) => (childrenMap[p.id] || []).length > 0);

  return (
    <div className={styles.Page}>
      <Helmet>
        <title>{`Проект #${order.id} — ЛОГМОЛЛ`}</title>
      </Helmet>

      <PageHeader
        title={`Проект #${order.id}`}
        subtitle={formatDate(order.createdAt)}
      />

      <div className={styles.StatusHero}>
        <span
          className={styles.StatusHeroBg}
          style={{ color: getComplexOrderStatusTextColor(status) }}
          aria-hidden="true"
        >
          {getComplexOrderStatusText(status)}
        </span>
        <span
          className={styles.StatusHeroLabel}
          style={{ color: getComplexOrderStatusTextColor(status) }}
        >
          {getComplexOrderStatusText(status)}
        </span>
      </div>

      <div className={styles.Content}>
        {items.length > 0 && (
          <div className={styles.Section}>
            <h2 className={styles.SectionTitle}>Состав</h2>
            <div className={styles.TreeList}>
              {hasTree
                ? parents.map((parent: any) => {
                    const children = childrenMap[parent.id] || [];
                    return (
                      <div key={parent.id} className={styles.TreeGroup}>
                        <div className={styles.ItemRow}>
                          <span className={styles.ItemName}>
                            {parent.name}
                          </span>
                          <span className={styles.ItemQty}>
                            × {parent.quantity}
                          </span>
                          {parent.total > 0 && (
                            <span className={styles.ItemPrice}>
                              {parent.total} ₽
                            </span>
                          )}
                        </div>
                        {children.length > 0 && (
                          <div className={styles.Children}>
                            {children.map((child: any) => (
                              <div key={child.id} className={styles.ChildRow}>
                                <span className={styles.ChildName}>
                                  {child.name}
                                </span>
                                <span className={styles.ChildQty}>
                                  × {child.quantity}
                                </span>
                                {child.total > 0 && (
                                  <span className={styles.ChildPrice}>
                                    {child.total} ₽
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                : items.map((item: any, index: number) => (
                    <div key={item.id ?? index} className={styles.ItemRow}>
                      <span className={styles.ItemName}>{item.name}</span>
                      <span className={styles.ItemQty}>× {item.quantity}</span>
                      {item.total > 0 && (
                        <span className={styles.ItemPrice}>
                          {item.total} ₽
                        </span>
                      )}
                    </div>
                  ))}
            </div>
          </div>
        )}

        {order.data && order.data.length > 0 && (
          <div className={styles.Section}>
            <h2 className={styles.SectionTitle}>Детали проекта</h2>
            <div className={styles.List}>
              {order.data.map((field, index) => (
                <div key={field.id ?? index} className={styles.ItemRow}>
                  <span className={styles.ItemName}>
                    {field.fieldTemplate?.name ||
                      `Поле #${field.fieldTemplateId}`}
                  </span>
                  <span className={styles.ItemValue}>{String(field.value)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {order.phone_number && (
          <div className={styles.Section}>
            <h2 className={styles.SectionTitle}>Телефон</h2>
            <div className={styles.InfoCard}>{order.phone_number}</div>
          </div>
        )}

        {order.notes && (
          <div className={styles.Section}>
            <h2 className={styles.SectionTitle}>Комментарий</h2>
            <div className={styles.NotesCard}>{order.notes}</div>
          </div>
        )}

        {order.total > 0 && (
          <div className={styles.TotalSection}>
            <div className={styles.TotalRow}>
              <span>Итого</span>
              <span>{order.total} ₽</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};